import { fromValue, map, pipe } from 'wonka';
import { Tag } from '../tag';
import { logError, logInfo } from '../logger';

const isMPUFormat = (format: string) => format.toLowerCase().includes('mpu');
const isProFormat = (format: string) =>
  ['square', 'landscape', 'portrait'].includes(format.toLowerCase());

/**
 * Default noCenter constraint for MPU creatives if not set
 */
export const setNoCenterForMPU = function (tag: Tag) {
  const adPayload = tag.payload;
  const isDesktop = adPayload?.user?.env.isDesktop;

  if (
    isDesktop &&
    isMPUFormat(adPayload.creative.internalFormat || '') &&
    !Object.prototype.hasOwnProperty.call(
      tag.pageOptions.constraints,
      'noCenter',
    )
  ) {
    tag.pageOptions.constraints = {
      ...tag.pageOptions.constraints,
      noCenter: 1,
    };
  }

  return tag;
};

/**
 * Default creative scale unit to false for pro formats
 */
const setProFormat = (tag: Tag) => {
  const adPayload = tag.payload!;

  adPayload.creative.proFormat = !!isProFormat(
    adPayload.creative.internalFormat || '',
  );

  return tag;
};

/**
 * Apply style overrides to ancestor elements based on StyleOverride constraints
 * When tag is destroyed, tidy up the style overrides back to original state
 */
const applyAncestorStyleOverrides = (tag: Tag) => {
  const { constraints } = tag.pageOptions;

  if (!constraints.styleOverrides) {
    return tag;
  }

  // Selectors may be in unsafe environments where we don't have access to the DOM
  // protect against this by wrapping in a try/catch
  try {
    constraints.styleOverrides.forEach(({ format, selector, styles }) => {
      // If no format, apply style, if format(s) only apply to matched formats
      if (
        format &&
        !format.includes(
          (tag.payload?.creative.internalFormat || '').toLowerCase(),
        )
      ) {
        return;
      }

      const elementToOverride = tag.container?.closest<HTMLElement>(selector);

      if (!elementToOverride) {
        logInfo(`No element found for selector ${selector}`);
        return;
      }

      styles.forEach(({ property, value, priority }) => {
        logInfo(
          `Applying style override "${property}: ${value} ${
            priority || ''
          }" to ${selector}`,
        );
        const originalInlineStyle =
          elementToOverride.style.getPropertyValue(property);

        elementToOverride.style.setProperty(property, value, priority);

        // Restore the element back to it's original state when the tag is destroyed
        tag.cleanupFunctions.push(() => {
          if (originalInlineStyle) {
            elementToOverride.style.setProperty(property, originalInlineStyle);
          } else {
            elementToOverride.style.removeProperty(property);
          }
        });
      });
    });
  } catch (e) {
    logError({
      tagId: tag.tagId,
      reason: 'lib-err-style-override',
      detail: `failed to apply style overrides ${JSON.stringify(
        constraints.styleOverrides,
      )}`,
      error: (e as Error).stack || e,
      impressionId: tag.pageOptions.iid || '',
    });
  }

  return tag;
};

export const applyRules$ = (tag: Tag) =>
  pipe(
    fromValue(tag),
    map(setNoCenterForMPU),
    map(setProFormat),
    map(applyAncestorStyleOverrides),
  );
